import {
  Heading,
  Text,
  Grid,
  GridItem,
  VStack,
  Button,
} from "@chakra-ui/react";
import { AirportSummary } from "./AirportSummary";
import { getDistance } from "@/utils/airport_distance";
import { Flight } from "@/models/flight";
import { t } from "i18next";
import { Ad } from "../Ad/ad";

interface RoundSummaryProps {
  points: number;
  flight: Flight;
  onNextRound: () => void;
  finalRoundComplete: boolean;
}

export const RoundSummary = ({
  points,
  flight,
  finalRoundComplete,
  onNextRound,
}: RoundSummaryProps) => {
  const distance = getDistance(
    flight.origin_airport,
    flight.destination_airport
  );

  return (
    <>
      <VStack h="100%" justify="center">
        <Text
          color="white"
          textAlign="center"
          fontSize={{ base: "4xl", md: "7xl" }}
          border="2px solid white"
          borderRadius="xl"
          p={{ base: 4, md: 10 }}
          mb={{ base: 0, md: 4 }}
          background="whiteAlpha.200"
        >
          {points.toLocaleString(navigator.language)}
        </Text>
        <Heading
          color="white"
          textAlign="center"
          fontSize={{ base: "xl", md: "3xl" }}
        >
          {t("game.roundSummary.pointsScored")}
        </Heading>

        <Grid
          templateColumns={{ base: "1fr", md: "1fr 1fr 1fr" }}
          gap={6}
          p={4}
          alignItems="center"
        >
          {/* Left Section */}
          <AirportSummary
            iata={flight.origin_airport.iata}
            name={flight.origin_airport.name}
            city={flight.origin_airport.city}
            country={
              flight.origin_airport.country_code === "US"
                ? flight.origin_airport.state_name
                : flight.origin_airport.country_name
            }
            emoji={flight.origin_airport.country_emoji}
          />

          {/* Middle Section */}
          <GridItem
            fontFamily="sans-serif"
            display={{ base: "none", md: "block" }}
          >
            <VStack align={{ base: "left", lg: "center" }}>
              <Text
                color="white"
                fontSize={{ base: "2xl", lg: "xl" }}
                fontFamily="Airline"
                mb={4}
                textDecoration={{ base: "none", lg: "underline" }}
                lineHeight="25px"
              >
                {t("game.roundSummary.flightDetails")} ({flight.flight_iata})
              </Text>
              <Text color="white" fontSize="xl">
                <strong>{t("game.roundSummary.airline")}</strong>
                {flight.airline}
              </Text>
              <Text color="white" fontSize="xl">
                <strong>{t("game.roundSummary.distance")}</strong>
                {distance.toLocaleString(navigator.language)}{" "}
                {t("game.roundSummary.distanceUnit")}
              </Text>
              <Text color="white" fontSize="xl">
                <strong>{t("game.roundSummary.duration")}</strong>
                {flight.flight_duration} {t("game.roundSummary.durationUnit")}
              </Text>
              <Text color="white" fontSize="xl">
                <strong>{t("game.roundSummary.plane")}</strong>
                {flight.aircraft_name}
              </Text>
            </VStack>
          </GridItem>

          {/* Right Section */}
          <AirportSummary
            iata={flight.destination_airport.iata}
            name={flight.destination_airport.name}
            city={flight.destination_airport.city}
            country={
              flight.destination_airport.country_code === "US"
                ? flight.destination_airport.state_name
                : flight.destination_airport.country_name
            }
            emoji={flight.destination_airport.country_emoji}
          />
        </Grid>
        <Button
          background="green.600"
          size="xl"
          onClick={onNextRound}
          w={{ base: "100%", md: "60%" }}
          h="70px"
          borderRadius="10px"
          fontSize="2xl"
          mt={10}
          _hover={{ background: "green.700" }}
        >
          {finalRoundComplete
            ? t("game.roundSummary.viewGameSummary")
            : t("game.roundSummary.nextRound")}
        </Button>
        <Ad slot="9373418011" mt={10} />
      </VStack>
    </>
  );
};
